import React, { useEffect, useState } from "react";
import {
  TextField,
  Button,
  Grid2,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Container,
  Typography,
  Paper,
  SelectChangeEvent,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Gender } from "../../model/common/Gender";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { Member, memberInit } from "../../model/entity/Member";
import {
  memberValidationObjInit,
  validateMember,
} from "../../model/validation/MemberValidation";
import handleAxiosError from "../../components/ErrorHandler";
import { notifErr, notifOk } from "../../components/Functions";
import { postFetcher } from "../../components/Fetchers";

const NewMember = () => {
  const navigate = useNavigate();
  const [triedSubmit, setTriedSubmit] = useState(false);
  const [member, setMember] = useState<Member>(memberInit);
  const [memberErr, setMemberErr] = useState(memberValidationObjInit);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMember({ ...member, [e.target.name]: e.target.value });
  };

  const handleGenderInputChange = (e: SelectChangeEvent) => {
    setMember({ ...member, [e.target.name]: e.target.value });
  };

  useEffect(() => {
    if (triedSubmit) setMemberErr(validateMember(member));
  }, [member, triedSubmit]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTriedSubmit(true);

    const memberErrors = validateMember(member);
    setMemberErr(memberErrors);

    if (Object.values(memberErrors).some((val) => val === false)) return;

    try {
      await postFetcher(`${process.env.REACT_APP_API_URL}/members`, member);
      notifOk("Člen úspěšně registrován.");
      navigate("/overview/members");
    } catch (err) {
      notifErr(handleAxiosError(err));
    }
  };

  const handleCancel = () => {
    navigate("/overview/members");
  };

  return (
    <Container
      maxWidth="md"
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        pb: "2rem",
      }}
    >
      <Paper elevation={3} sx={{ padding: "1.5rem" }}>
        <Typography variant="h5" sx={{ marginBottom: "1.5rem" }}>
          Registrace člena
        </Typography>
        <Grid2 container spacing={3} size={{ xs: 12, md: 6 }}>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              size="small"
              label="Jméno"
              required
              name="firstName"
              value={member.firstName}
              error={!memberErr.firstName}
              onChange={handleInputChange}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              size="small"
              label="Příjmení"
              required
              name="lastName"
              value={member.lastName}
              error={!memberErr.lastName}
              onChange={handleInputChange}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                format="DD.MM.YYYY"
                label="Datum narození"
                sx={{ width: "100%" }}
                name="birthDate"
                value={member.birthDate === "" ? null : dayjs(member.birthDate)}
                onChange={(date) =>
                  setMember({
                    ...member,
                    birthDate: date ? date.format("YYYY-MM-DD") : "",
                  })
                }
                slotProps={{
                  textField: {
                    size: "small",
                    required: true,
                    error:
                      !memberErr.birthDate &&
                      !dayjs(member.birthDate, "YYYY-MM-DD", true).isValid(),
                  },
                  field: { clearable: true },
                }}
              />
            </LocalizationProvider>
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <FormControl required size="small" fullWidth>
              <InputLabel>Pohlaví</InputLabel>
              <Select
                label="Pohlaví"
                name="gender"
                value={member.gender}
                error={!memberErr.gender}
                onChange={handleGenderInputChange}
              >
                <MenuItem value={Gender.MALE}>Muž</MenuItem>
                <MenuItem value={Gender.FEMALE}>Žena</MenuItem>
              </Select>
            </FormControl>
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              size="small"
              label="Email"
              type="email"
              required
              name="email"
              value={member.email}
              error={!memberErr.email}
              onChange={handleInputChange}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              size="small"
              label="Telefon"
              type="tel"
              required
              name="phone"
              value={member.phone}
              error={!memberErr.phone}
              onChange={handleInputChange}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              size="small"
              label="Adresa bydliště"
              required
              name="address"
              value={member.address}
              error={!memberErr.address}
              onChange={handleInputChange}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              size="small"
              label="Rodné číslo (bez lomítka)"
              name="nationalId"
              value={member.nationalId}
              error={!memberErr.nationalId}
              onChange={handleInputChange}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              size="small"
              label="Poznámka"
              multiline
              rows={4}
              name="note"
              value={member.note}
              error={!memberErr.note}
              onChange={handleInputChange}
            />
          </Grid2>

          <Grid2 container size={{ xs: 12 }} sx={{ justifyContent: "right", gap: 2 }}>
            <Grid2>
              <Button
                variant="outlined"
                className="cancelBtn"
                onClick={handleCancel}
              >
                Zrušit
              </Button>
            </Grid2>
            <Grid2>
              <Button
                variant="outlined"
                className="submitBtn"
                onClick={handleSubmit}
              >
                Přidat
              </Button>
            </Grid2>
          </Grid2>
        </Grid2>
      </Paper>
    </Container>
  );
};

export default NewMember;
